import React, { lazy, Suspense, useEffect, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

// Lazy-load DevTool only in development to avoid bundling devDependency in production
const DevTool =
  process.env.NODE_ENV === 'development'
    ? lazy(() =>
        import('@hookform/devtools').then((module) => ({
          default: module.DevTool,
        })),
      )
    : () => null;

import Amex from '../../../assets/images/AmericanExpressIcon.svg';
import Discover from '../../../assets/images/DiscoverIcon.svg';
import Mastercard from '../../../assets/images/MastercardIcon.svg';
import Visa from '../../../assets/images/VisaIcon.svg';
import * as service from '../../../utils/service';
import Spinner from '../../Spinner/Spinner';
import { FORM_VALIDATION, US_STATES } from '../constants';
import {
  ErrorMessage,
  FieldLabel,
  inputClassName,
  TextField,
} from './FormFields';

// Stripe Element options - hoisted to module scope for stable reference
const cardElementOptions = {
  style: {
    base: {
      color: '#4A4A4D',
      fontSize: '16px',
      '::placeholder': { color: '#626366' },
    },
    invalid: { color: '#e35256' },
  },
};

// Main CheckoutForm component
const CheckoutFormComponent = ({
  onSubmit,
  selectedSubscription,
  cacheData,
  defaultEmail = '',
  isAdmin = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
    control,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      email: defaultEmail,
      mobile: '',
      street: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      cardholderName: '',
      termsAccepted: false,
    },
  });
  const stripe = useStripe();
  const elements = useElements();

  const [showSpinner, setShowSpinner] = useState(false);
  const [cardNumberError, setCardNumberError] = useState(
    /** @type {string|null} */ (null),
  );
  const [cardExpiryError, setCardExpiryError] = useState(
    /** @type {string|null} */ (null),
  );
  const [cardCvcError, setCardCvcError] = useState(
    /** @type {string|null} */ (null),
  );

  // Combine RHF's isValid with Stripe element error checks
  const isFormComplete =
    isValid && !cardNumberError && !cardExpiryError && !cardCvcError;

  const onFormSubmit = async (data) => {
    if (!stripe || !elements) {
      return;
    }

    setShowSpinner(true);
    setCardNumberError(null);
    setCardExpiryError(null);
    setCardCvcError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          email: data.email,
          name: data.cardholderName,
          phone: data.mobile,
          address: {
            line1: data.street,
            line2: data.address2 || '',
            city: data.city,
            state: data.state,
            postal_code: data.zip,
            country: 'US',
          },
        },
      });

      if (error) {
        setCardNumberError(error.message || '');
        setShowSpinner(false);
        return;
      }

      // Determine plan code based on user type
      // planCode is an object with 'provider' and 'admin' keys
      const planCode = isAdmin
        ? selectedSubscription?.planCode?.admin
        : selectedSubscription?.planCode?.provider;

      // Create CreateSubscriptionRequest payload
      const subscriptionObject = {
        UserId: cacheData?.userId || '', // From cache data
        ProviderSelectionCacheId: cacheData?.providerSelectionCacheId || '', // From cache data
        Email: data.email,
        MobileNumber: data.mobile,
        StreetAddress: data.street,
        StreetAddress2: data.address2 || '',
        City: data.city,
        State: data.state,
        Zip: data.zip,
        StripeToken: paymentMethod.id,
        PlanCode: planCode,
        AcceptUserAgreement: data.termsAccepted,
        PromoCode: data.promoCode || '',
        PlanType: selectedSubscription?.billingCycle || 'yearly',
        ProviderIds: cacheData?.selectedProviders?.map((p) => p.pwid) || [],
      };

      // Call the API directly
      await createSubscription(subscriptionObject);
    } catch (error) {
      console.error('Payment processing error:', error);
      setCardNumberError('Unable to process payment at this time.');
      setShowSpinner(false);
    }
  };

  // Create subscription function
  const createSubscription = async (subscriptionObject) => {
    try {
      const url = `${service.baseurl()}/selfserve/subscriptions`;
      const response = await service.post(url, subscriptionObject, '2');

      // Handle both camelCase and PascalCase response formats
      const subscribers = response.subscribers || [];
      const failures = response.failures || [];

      const hasSubscribers = subscribers.length > 0;
      const hasFailures = failures.length > 0;

      if (hasSubscribers && !hasFailures) {
        // All subscriptions succeeded
        // Don't hide spinner here - let redirect spinner handle it
        onSubmit?.({
          success: true,
          message: 'Subscription created successfully!',
          data: response,
        });
      } else if (hasSubscribers && hasFailures) {
        // Partial success
        setShowSpinner(false);
        setCardNumberError(
          `Subscription created for ${subscribers.length} provider(s), but ${failures.length} failed. Please contact support.`,
        );
      } else if (hasFailures && !hasSubscribers) {
        // Complete failure
        setShowSpinner(false);
        setCardNumberError(
          'Subscription failed for all providers. Please try again or contact support.',
        );
      } else {
        // Unknown response format
        setShowSpinner(false);
        setCardNumberError(
          'Unexpected response from server. Please contact support.',
        );
      }
    } catch (err) {
      let message = '';
      if (err.response?.status === 504) {
        message = `We're currently processing your subscription. If you haven't received a confirmation email shortly, please try again later or reach out to our support team at elevatedprofile@healthgrades.com.`;
      } else {
        message = err.message || 'Unable to process subscription at this time.';
      }
      setShowSpinner(false);
      setCardNumberError(message);
    }
  };

  const { onChange: onTermsChange, ...termsRegister } = register(
    'termsAccepted',
    FORM_VALIDATION.termsAccepted,
  );

  return (
    <div className="flex flex-col items-start gap-4 self-stretch lg:p-0 md:p-0">
      <h2 className="text-2xl font-semibold leading-[30px] text-gray-90 self-stretch">
        Enter payment details
      </h2>

      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="flex flex-col items-start gap-2 self-stretch"
      >
        <TextField
          label="Email"
          type="email"
          placeholder={isAdmin ? 'Administrator email' : 'Email'}
          error={errors.email}
          registration={register('email', FORM_VALIDATION.email)}
        />

        <TextField
          label="Mobile number"
          type="tel"
          placeholder={isAdmin ? 'Administrator phone number' : 'Phone Number'}
          error={errors.mobile}
          registration={register('mobile', FORM_VALIDATION.mobile)}
        />

        <TextField
          label="Street address"
          placeholder="Enter street address"
          error={errors.street}
          registration={register('street', FORM_VALIDATION.street)}
        />

        <TextField
          label="Address 2"
          placeholder="Apt, suite, etc."
          registration={register('address2')}
          optional
        />

        <TextField
          label="City"
          placeholder="Enter city"
          error={errors.city}
          registration={register('city', FORM_VALIDATION.city)}
        />

        {/* State, ZIP */}
        <div className="flex items-start gap-2 shrink-0 self-stretch md:flex-row flex-col">
          <FieldLabel
            label="State"
            className="grow shrink-0 w-[236px] md:w-[236px] sm:w-full"
          >
            <select
              className={inputClassName}
              {...register('state', FORM_VALIDATION.state)}
              defaultValue=""
            >
              {US_STATES.map((state) => (
                <option
                  key={state.value}
                  value={state.value}
                  disabled={state.disabled}
                >
                  {state.label}
                </option>
              ))}
            </select>
            <ErrorMessage>{errors.state?.message}</ErrorMessage>
          </FieldLabel>

          <TextField
            label="ZIP"
            placeholder="28226"
            error={errors.zip}
            registration={register('zip', FORM_VALIDATION.zip)}
            className="grow shrink-0 w-[236px] md:w-[236px] sm:w-full"
          />
        </div>

        {/* Payment Method */}
        <div className="flex justify-between items-center gap-6 shrink-0 self-stretch my-4">
          <span className="text-base font-semibold leading-6 text-gray-70">
            Payment method
          </span>
          <div className="flex items-start gap-[3px]">
            <img
              src={Visa}
              alt="Visa"
              className="h-[18px] w-auto rounded-[1.5px]"
            />
            <img
              src={Mastercard}
              alt="Mastercard"
              className="h-[18px] w-auto rounded-[1.5px]"
            />
            <img
              src={Amex}
              alt="Amex"
              className="h-[18px] w-auto rounded-[1.5px]"
            />
            <img
              src={Discover}
              alt="Discover"
              className="h-[18px] w-auto rounded-[1.5px]"
            />
          </div>
        </div>

        {/* Card Number */}
        <FieldLabel label="Card number">
          <div
            className={`${inputClassName} min-h-[44px] focus-within:border-blue-50`}
          >
            <CardNumberElement
              className="w-full p-0 border-none outline-none text-base font-normal leading-6 text-gray-90 block min-h-[20px]"
              onChange={(event) => {
                if (event.error) {
                  setCardNumberError(event.error.message);
                } else {
                  setCardNumberError(null);
                }
              }}
              options={cardElementOptions}
            />
          </div>
          <ErrorMessage>{cardNumberError || ''}</ErrorMessage>
        </FieldLabel>

        {/* Expiration and CVC */}
        <div className="flex items-start gap-2 shrink-0 self-stretch md:flex-row flex-col">
          <FieldLabel
            label="Expiration"
            className="grow shrink-0 w-[236px] md:w-[236px] sm:w-full"
          >
            <div
              className={`${inputClassName} min-h-[44px] focus-within:border-blue-50`}
            >
              <CardExpiryElement
                className="w-full p-0 border-none outline-none text-base font-normal leading-6 text-gray-90 block min-h-[20px]"
                onChange={(event) => {
                  if (event.error) {
                    setCardExpiryError(event.error.message);
                  } else {
                    setCardExpiryError(null);
                  }
                }}
                options={cardElementOptions}
              />
            </div>
            <ErrorMessage>{cardExpiryError || ''}</ErrorMessage>
          </FieldLabel>

          <FieldLabel
            label="Security"
            className="grow shrink-0 w-[236px] md:w-[236px] sm:w-full"
          >
            <div
              className={`${inputClassName} min-h-[44px] focus-within:border-blue-50`}
            >
              <CardCvcElement
                className="w-full p-0 border-none outline-none text-base font-normal leading-6 text-gray-90 block min-h-[20px]"
                onChange={(event) => {
                  if (event.error) {
                    setCardCvcError(event.error.message);
                  } else {
                    setCardCvcError(null);
                  }
                }}
                options={cardElementOptions}
              />
            </div>
            <ErrorMessage>{cardCvcError || ''}</ErrorMessage>
          </FieldLabel>
        </div>

        <TextField
          label="Cardholder name"
          placeholder="Full name on card"
          error={errors.cardholderName}
          registration={register(
            'cardholderName',
            FORM_VALIDATION.cardholderName,
          )}
        />

        {/* 
          TODO: Uncomment promo code field once backend checkout session support is implemented
          The promo code field is temporarily hidden until backend support is ready.
          To restore: Remove comment tags and add validation if needed.
        */}
        {/* Promo Code */}
        {/* <TextField
          label="Promo code"
          placeholder="Enter promo code"
          registration={register('promoCode')}
        /> */}

        {/* Terms and Submit */}
        <div className="flex flex-col items-end gap-4 self-stretch mt-2">
          {/* Terms and Conditions */}
          <FieldLabel label="" className="py-5 cursor-pointer">
            <div className="flex items-start gap-2 shrink-0 self-stretch">
              <input
                type="checkbox"
                {...termsRegister}
                className="w-5 h-5 rounded-sm border border-gray-90 shrink-0 cursor-pointer mt-0 accent-blue-50"
                onChange={(e) => {
                  /**
                   * We need to immediate validate the form on any changes
                   * waiting for blur was confusing UX since the checkbox
                   * was at the end of the form and user had to click
                   * submit to see the error.
                   */

                  onTermsChange(e); // Let RHF update the value first

                  // revalidate all fields on change
                  trigger();
                }}
              />
              <span className="text-gray-90 grow text-xs font-extralight leading-4">
                By clicking Submit below, you are purchasing a Healthgrades
                Elevated Profile subscription. You may cancel your subscription
                by visiting the Account Settings in your HG Pro account.
                Subscriptions will automatically renew until canceled. For
                additional help,{' '}
                <a
                  href="mailto:elevatedprofile@healthgrades.com"
                  className="text-blue-50 underline hover:no-underline"
                >
                  Contact Us.
                </a>
                <br />
                <br />
                By clicking Submit, you also agree to our{' '}
                <a
                  href="/?policy=terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-50 underline hover:no-underline"
                >
                  Terms of Use
                </a>
                , which includes our{' '}
                <a
                  href="/?policy=terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-50 underline hover:no-underline"
                >
                  Subscription Terms
                </a>
                , and acknowledge our{' '}
                <a
                  href="https://www.healthgrades.com/content/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-50 underline hover:no-underline"
                >
                  Privacy Policy.
                </a>
              </span>
            </div>
            <ErrorMessage>{errors.termsAccepted?.message}</ErrorMessage>
          </FieldLabel>
          <button
            type="submit"
            disabled={!isFormComplete || showSpinner}
            className="bg-deep-blue-30 w-[200px] flex justify-center items-center gap-2.5 py-3 px-12 shrink-0 rounded-full border-none cursor-pointer transition-all duration-300 hover:bg-deep-blue-50 disabled:bg-gray-20 disabled:text-blue-30 disabled:cursor-not-allowed disabled:opacity-60 lg:w-[200px] md:w-full md:self-stretch"
          >
            {showSpinner ? (
              <Spinner cta={true} />
            ) : (
              <span className="text-white text-sm font-semibold leading-5 text-center">
                Submit
              </span>
            )}
          </button>
        </div>
      </form>

      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <DevTool control={control} />
        </Suspense>
      )}
    </div>
  );
};

CheckoutFormComponent.propTypes = {
  onSubmit: PropTypes.func,
  selectedSubscription: PropTypes.object,
  cacheData: PropTypes.object,
  defaultEmail: PropTypes.string,
  isAdmin: PropTypes.bool,
};

// Wrapper component with Stripe Elements provider
const CheckoutForm = ({
  onSubmit,
  selectedSubscription,
  stripePublishableKey,
  cacheData,
  defaultEmail = '',
  isAdmin = false,
}) => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  if (!stripePromise) {
    return <Spinner cta={true} />;
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutFormComponent
        onSubmit={onSubmit}
        selectedSubscription={selectedSubscription}
        cacheData={cacheData}
        defaultEmail={defaultEmail}
        isAdmin={isAdmin}
      />
    </Elements>
  );
};

CheckoutForm.propTypes = {
  onSubmit: PropTypes.func,
  selectedSubscription: PropTypes.object,
  stripePublishableKey: PropTypes.string,
  cacheData: PropTypes.object,
  defaultEmail: PropTypes.string,
  isAdmin: PropTypes.bool,
};

export default CheckoutForm;
